import { useEffect, useMemo, useState } from 'react'
import Button from '@/shared/Buttons/Button'
import SearchInput from '@/shared/Inputs/SearchInput'
import Table, { ITableField } from '@/shared/Tables/Table'
import useRegularTable from '@/shared/Tables/Table/ctrl/useRegularTable'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import DownloadIcon from '@/assets/icons/download.svg?react'
import alertService from '@/services/alerts/alert.service'
import cmsApi from '../cms.api'
import { IEmailSubscription } from '../cms.types'
import css from '../cms.module.css'

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const formatDateTime = (value: string) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return dateTimeFormatter.format(date)
}

const formatBoolean = (value: boolean) => (value ? 'Да' : 'Нет')

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const EmailSubscriptionsWorkspace = () => {
  const [items, setItems] = useState<IEmailSubscription[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [search, setSearch] = useState('')

  const load = async () => {
    setLoading(true)
    const response = await cmsApi.listEmailSubscriptions()
    setLoading(false)

    if (response.status === 'success') {
      setItems(response.body)
      return
    }

    alertService.requestAlert({ type: 'error', title: 'Не удалось загрузить подписчиков', description: response.message })
  }

  useEffect(() => {
    load()
  }, [])

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return items

    return items.filter((item) => {
      return (
        item.email.toLowerCase().includes(normalizedSearch) ||
        String(item.id).includes(normalizedSearch) ||
        formatBoolean(item.subExclusiveContent).toLowerCase().includes(normalizedSearch) ||
        formatBoolean(item.subHeadliner).toLowerCase().includes(normalizedSearch) ||
        formatDateTime(item.createdAt).toLowerCase().includes(normalizedSearch)
      )
    })
  }, [items, search])

  const fields = useMemo<ITableField<IEmailSubscription, keyof IEmailSubscription | string>[]>(
    () => [
      {
        key: 'id',
        header: 'ID',
        width: '90px',
        getter: (item) => String(item.id),
        sortGetter: (item) => item.id,
      },
      {
        key: 'email',
        header: 'Email',
        width: '360px',
        getter: (item) => item.email,
        sortGetter: (item) => item.email,
      },
      {
        key: 'subExclusiveContent',
        header: 'Эксклюзивный контент',
        width: '190px',
        getter: (item) => formatBoolean(item.subExclusiveContent),
        sortGetter: (item) => Number(item.subExclusiveContent),
      },
      {
        key: 'subHeadliner',
        header: 'Хедлайнер',
        width: '140px',
        getter: (item) => formatBoolean(item.subHeadliner),
        sortGetter: (item) => Number(item.subHeadliner),
      },
      {
        key: 'createdAt',
        header: 'Дата подписки',
        width: '180px',
        getter: (item) => formatDateTime(item.createdAt),
        sortGetter: (item) => new Date(item.createdAt).getTime(),
      },
      {
        key: 'updatedAt',
        header: 'Обновлено',
        width: '180px',
        getter: (item) => formatDateTime(item.updatedAt),
        sortGetter: (item) => new Date(item.updatedAt).getTime(),
      },
    ],
    []
  )

  const table = useRegularTable({
    fields,
    itemKey: 'id',
    items: filteredItems,
    tableKey: 'cms/emailSubscriptions',
  })

  const handleExport = async () => {
    setExporting(true)
    const response = await cmsApi.exportEmailSubscriptions()
    setExporting(false)

    if (response.status === 'error') {
      alertService.requestAlert({ type: 'error', title: 'Не удалось экспортировать XLSX', description: response.message })
      return
    }

    downloadBlob(response.body, 'email-subscriptions.xlsx')
  }

  return (
    <>
      <div className={css.toolbar}>
        <div>
          <h1 className="text-heading-4">Email подписчики</h1>
          <p className="text-nm text-description">{items.length} подписчиков</p>
        </div>
        <div className="inline-flex-gap gap8 center">
          <div style={{ width: 300 }}>
            <SearchInput value={search} name="search" onChange={(value) => setSearch(value)} placeHolder="Поиск" />
          </div>
          <Button
            size="default"
            type="primary"
            text="Экспорт XLSX"
            icon={<DownloadIcon />}
            loading={exporting}
            disabled={loading || items.length === 0}
            onClick={handleExport}
          />
        </div>
      </div>

      <div className={css.tablePanel}>
        {loading && items.length === 0 ? (
          <div className={css.emptyState}>
            <ThreeDotsLoader />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className={css.emptyState}>
            <p className="text-nm text-description">Подписчики не найдены</p>
          </div>
        ) : (
          <Table
            fields={table.fields as ITableField<IEmailSubscription, keyof IEmailSubscription>[]}
            items={table.orderedItems}
            itemKey="id"
            loading={loading}
            totalItemsCount={table.totalItemsCount}
            pageNumber={table.pageNumber}
            itemsPerPage={table.itemsPerPage}
            orderingState={table.orderingState}
            onOrderingStateChange={table.onOrderingStateChange}
            onSwap={table.onSwap}
            resizePluginCtrl={table.resizePluginCtrl}
          />
        )}
      </div>
    </>
  )
}

export default EmailSubscriptionsWorkspace
